import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { CheckCircle2, InfoIcon } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import { cn } from "@shared/lib/utils";
import {
  useSystemAccessModules,
  useUpdatSystemAdminAccess,
} from "../../../hooks"; // <-- your hook
import {
  useRemoteUtilityId,
  useTenantId,
} from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";

const SelectionItem = ({
  icon: Icon,
  name,
  description,
  selected,
  onClick,
  disabled = false,
}: {
  icon?: React.ElementType;
  name: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <div
    className={cn(
      "flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all",
      selected
        ? "border-primary bg-primary/5"
        : "border-border hover:border-primary/50",
      disabled && "opacity-50 cursor-not-allowed"
    )}
    onClick={disabled ? undefined : onClick}
  >
    <div
      className={cn(
        "p-2 rounded-md mr-3",
        selected ? "bg-primary/10" : "bg-muted"
      )}
    >
      {Icon && (
        <Icon
          className={cn(
            "h-5 w-5",
            selected ? "text-primary" : "text-muted-foreground"
          )}
        />
      )}
    </div>
    <div className="flex-1">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">{name}</h3>
        {selected && <CheckCircle2 className="h-5 w-5 text-primary" />}
      </div>
      <p className="text-sm text-muted-foreground mt-1">{description}</p>
    </div>
  </div>
);

const SystemAccessTab: React.FC = () => {
  const tenantId = useTenantId();
  const utilityId = useRemoteUtilityId();

  const { data: modules = [] } = useSystemAccessModules();
  const { mutate: updateSystemAccess } = useUpdatSystemAdminAccess();

  const toggleModule = (module: any) => {
    const payload = {
      module_id: module.id,
      tenant_id: Number(tenantId),
      utility_id: Number(utilityId),
      active: (!module.active).toString(),
    };
    //Commented the API call as per the new requirements

   /*
    updateSystemAccess(payload, {
          onSuccess: () => {
            logEvent('System Access Module Selection', {
              ...payload,
              module_name: module.displayName
            })
          }
        });
        
    */
  };

  return (
    <div className="transition-opacity duration-500 opacity-100">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-medium">
           System Access
          </CardTitle>
          <CardDescription className="page-subtitle">
            Select which system modules your organization will use
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="mb-4">
            <AccordionItem value="help">
              <AccordionTrigger className="text-sm font-medium">
                <div className="flex items-center">
                  <InfoIcon className="h-4 w-4 mr-2" />
                  Need help with system modules?
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">
                <p className="mb-2">
                  System modules control what functionality is available in your
                  utility management system:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>
                    Only selected modules will appear in your navigation menu
                  </li>
                  <li>
                    User roles will be configured based on the enabled modules
                  </li>
                  <li>
                    Start with essential modules and add more as your team gets
                    comfortable
                  </li>
                  <li>
                    Some modules depend on others - recommendations will appear
                    as you select
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {modules.map((module) => (
              <SelectionItem
                key={module.id}
                name={module.displayName}
                description={module.description}
                selected={module.active}
                onClick={() => toggleModule(module)}
                disabled={!module.editable}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemAccessTab;
